/* !
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
  ArrayExtensions,
  getFallbackImageUrl,
  ObjectExtensions,
} from "@msdyn365-commerce-modules/retail-actions";
import {
  getPayloadObject,
  getTelemetryAttributes,
  ITelemetryContent,
  IncrementalQuantity,
} from "@msdyn365-commerce-modules/utilities";
import {
  IComponentProps,
  IGridSettings,
  IImageSettings,
  Image,
  msdyn365Commerce,
} from "@msdyn365-commerce/core";
import {
  ChannelDeliveryOptionConfiguration,
  SimpleProduct,
} from "@msdyn365-commerce/retail-proxy";
import {
  CartLine,
  SalesLine,
} from "@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g";
import * as React from "react";
import PriceComponent from "./price.component";
import {getProductPageUrlSync} from "@msdyn365-commerce-modules/retail-actions";
import {ICoreContext} from "@msdyn365-commerce/core";
import {ICartResources} from "../../definition-extensions/cart.ext.props.autogenerated";
// import currencyFormatter from "currency-formatter";

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
export interface ICartlineResourceString {
  /** Display string for discount label */
  discountStringText: string;

  /** String for size key  */
  sizeString: string;

  /** String for color key  */
  colorString: string;

  /** String associated with the configuration product dimension  */
  configString: string;

  /** String for style key  */
  styleString: string;

  /** String for amount key  */
  amountString?: string;

  /** Display string for quantity label  */
  quantityDisplayString: string;

  /** Display string for quantity label  */
  inputQuantityAriaLabel: string;

  /** Aria label for the decrement button in quanitity component  */
  decrementButtonAriaLabel?: string;

  /** Aria label for the increment button in quanitity component  */
  incrementButtonAriaLabel?: string;

  /** Original text screenreader */
  originalPriceText: string;

  /** Current text screenreader  */
  currentPriceText: string;

  /** Shipping Charges Text  */
  shippingChargesText: string;
}

interface ICartLineData {
  cartLine: CartLine | SalesLine;
  product?: SimpleProduct;
}

export type CartLineDisplayMode = "DEFAULT" | "COMPACT";

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps
  extends IComponentProps<ICartLineData & ICartResources> {
  /** The flag to change the quantity component from interactible to static  */
  disableQuantityInteractivity?: boolean;

  /** The primary image url  */
  primaryImageUrl?: string;

  /** The product url  */
  productUrl?: string;

  /** GridSettings for the product image in cartLine  */
  gridSettings: IGridSettings;

  /** ImageSettings for the product image in cartLine  */
  imageSettings: IImageSettings;

  /** Boolean flag to indicate if the item is out of stock  */
  isOutOfStock?: boolean;

  /** Flag to make quantity section editable  */
  isQuantityEditable?: boolean;

  /** Max quantity for line item  */
  maxQuantity?: number;

  /** Current quantity for line item  */
  currentQuantity?: number;

  /** Resource string for the component  */
  resources: ICartlineResourceString;

  /** SalesLine flag  */
  isSalesLine?: boolean;

  /** Error message to show in place of quantity */
  errorMessage?: string;

  /** Display mode to use */
  displayMode?: CartLineDisplayMode;

  /** Inventory information label  */
  inventoryInformationLabel?: string;

  /** Inventory information class name  */
  inventoryLabelClassName?: string;

  /** Flag to show/hide shipping charges for line items  */
  showShippingChargesForLineItems?: boolean;

  /** Boolean flag to indicate if cart state status is ready  */
  isCartStateReady?: boolean;

  /**  Chanel Delivery Option configuration is from api. */
  channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

  /** The telemetry content */
  telemetryContent?: ITelemetryContent;

  /** Quantity onChange callback  */
  quantityOnChange?(cartLine: any, newQuantity: number): void;

  allProps: any;

  atp?: any;
}

interface IDimensionStrings {
  /** String for size key  */
  sizeString: string;

  /** String for color key  */
  colorString: string;

  /** String associated with the configuration product dimension  */
  configString: string;

  /** String for style key  */
  styleString: string;

  /** String for amount key  */
  amountString?: string;
}

const CartLineItemFunctions = {
  renderProductDimensions: (
    Product: SimpleProduct,
    DimensionStrings: IDimensionStrings,
  ) => {
    if (!Product || !Product.Dimensions) {
      return [];
    }

    return Product.Dimensions.map((productDimension: any) => {
      if (productDimension.DimensionTypeValue === 1) {
        return (
          <div
            key={`${Product.RecordId}ProductDimensions1`}
            className="msc-cart-line__product-variant-item"
          >
            <span className="msc-cart-line__product-variant-color">
              {DimensionStrings.colorString}:
              <span className="name">
                {productDimension.DimensionValue &&
                  productDimension.DimensionValue.Value}
              </span>
            </span>
          </div>
        );
      }

      if (productDimension.DimensionTypeValue === 2) {
        return (
          <div
            key={`${Product.RecordId}ProductDimensions2`}
            className="msc-cart-line__product-variant-item"
          >
            <span className="msc-cart-line__product-configuration">
              {DimensionStrings.configString}

              <span className="name">
                {productDimension.DimensionValue &&
                  productDimension.DimensionValue.Value}
              </span>
            </span>
          </div>
        );
      }

      if (productDimension.DimensionTypeValue === 3) {
        return (
          <div
            key={`${Product.RecordId}ProductDimensions3`}
            className="msc-cart-line__product-variant-item"
          >
            <span className="msc-cart-line__product-variant-size">
              {DimensionStrings.sizeString}
              <span className="name">
                {productDimension.DimensionValue &&
                  productDimension.DimensionValue.Value}
              </span>
            </span>
          </div>
        );
      }

      if (productDimension.DimensionTypeValue === 4) {
        return (
          <div
            key={`${Product.RecordId}ProductDimensions4`}
            className="msc-cart-line__product-variant-item"
          >
            <span className="msc-cart-line__product-variant-style">
              {Product.IsGiftCard
                ? DimensionStrings.amountString
                : DimensionStrings.styleString}
              <span className="name">
                {productDimension.DimensionValue &&
                  productDimension.DimensionValue.Value}
              </span>
            </span>
          </div>
        );
      }
      return null;
    });
  },
  renderDiscountLines: (props: any) => {
    if (
      !props.data?.cartline?.DiscountLines ||
      props.data.cartline?.DiscountLines?.length === 0
    ) {
      return null;
    }
    let price = props.data.cartline.Price * props.data.cartline.Quantity;
    /**
     * Get the updated discount percentage.
     * @param {DiscountLine} discountLine - The discount line.
     * @param {CartLine} cartLine - The cart line.
     * @returns {number} The updated discount percentage.
     */
    // const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
    //     const numberZero = 0;
    //     const decimalPrecision = 2;
    //     const fullPercentage = 100;
    //     let updatedPercent: number = discountLine.Percentage ?? numberZero;

    //     if ((discountLine.EffectivePercentage === numberZero ||
    //         (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
    //         cartLine.Price && cartLine.Price > numberZero && cartLine.Quantity && cartLine.Quantity !== numberZero) {
    //         const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;
    //         updatedPercent = Number(((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision));
    //     }

    //     return updatedPercent;
    // };

    // const discountAmountCalculate = (discountLines: any): any => {
    //     var sum: Number = 0;
    //     discountLines.map((element: any) => {
    //         if (element.CustomerDiscountTypeValue !== 3) {
    //             sum += element?.EffectiveAmount
    //         }
    //     })
    //     return sum;
    // }

    const PrevSum = (arr: any, currentIndex: any) => {
      var sum: number = 0;
      if (currentIndex == 0) {
        return 0;
      }
      for (var i = 0; i < currentIndex; i++) {
        sum += Number(arr[i].EffectiveAmount);
      }
      return sum;
    };

    return props.data.cartline?.DiscountLines?.map(
      (discountLine: any, index: any) => {
        if (discountLine.CustomerDiscountTypeValue != 3) {
          return (
            <div
              key={discountLine?.OfferId || index}
              className="msc-cart-line-item-product-discount"
            >
              <span className="msc-cart-line__product-savings-label">
                {discountLine?.OfferName ? discountLine?.OfferName : ""}
                {` ${props.resources.discountStringText || "Discount"} `}
              </span>
              <span className="msc-cart-line__promo-codes">
                <span>
                  {props.context.cultureFormatter.formatCurrency(
                    Number(discountLine.EffectiveAmount),
                  )}
                </span>

                {/* {props.isSalesLine ? <span>{props.context.cultureFormatter.formatCurrency(
                                  discountAmountCalculate(props.data.cartline.DiscountLines)
                              )}</span> : <span>{props.context.cultureFormatter.formatCurrency(
                                  Number(discountLine.DiscountCost)
                              )}</span> */}

                {/* discountLine?.DiscountCost && (
                                       <>
  
                                          <PriceComponent */}

                {/* data={ {
                                                   price: {
                                                       CustomerContextualPrice: props?.data?.cartline?.DiscountAmountWithoutTax,
                                                       BasePrice: props?.data?.cartline?.DiscountAmount
                                                   }
                                               }}
  
                                               context={props.context}
                                               id={props.id}
                                               typeName={props.typeName}
                                               className='msc-cart-line__discount-value'
                                               currencyCode={props.currencyCode}
                                           />
                                       </>
                                   ) */}
                {/* } */}
                {/* {discountLine.DiscountCost && (
                              <>
                              
                                  <PriceComponent
                                      // data={props.isSalesLine ? {
                                      //     price: {
                                      //         // @ts-expect-error
                                      //         CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                      //     }
                                      // } : {
                                      //     price: {
                                      //         CustomerContextualPrice: props.data.cartLine.DiscountAmountWithoutTax,
                                      //         BasePrice: props.data.cartLine.DiscountAmount
                                      //     }
                                      // }}
                                      data={ {
                                          price: {
                                              CustomerContextualPrice: props.data.cartLine.DiscountAmountWithoutTax,
                                              BasePrice: props.data.cartLine.DiscountAmount
                                          }
                                      }}
                                     
                                      context={props.context}
                                      id={props.id}
                                      typeName={props.typeName}
                                      className='msc-cart-line__discount-value'
                                  />
                              </>
                          )} */}
              </span>
              <span>
                {` (${
                  discountLine?.EffectivePercentage != 0
                    ? discountLine?.EffectivePercentage
                    : (
                        Number(
                          discountLine.EffectiveAmount /
                            (price -
                              PrevSum(
                                props.data.cartline.DiscountLines,
                                index,
                              )),
                        ) * 100
                      ).toFixed(2)
                }%)`}
                {/* {` (${discountLine?.EffectivePercentage !== undefined && discountLine?.EffectivePercentage != 0 ? discountLine?.EffectivePercentage : discountPercent}%)`} */}
              </span>
            </div>
          );
        } else {
          return <></>;
        }
      },
    );
  },
  renderInventoryLabel: (props: ICartLineProps) => {
    if (!props.inventoryInformationLabel) {
      return null;
    }
    const inventoryCssName = props.inventoryLabelClassName
      ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
      : "msc-cart-line__product-inventory-label";
    return (
      <span className={inventoryCssName}>
        {props.inventoryInformationLabel}
      </span>
    );
  },
  renderShippingLabel: (props: any) => {
    const pickupDeliveryModeCode =
      props.context.request.channel?.PickupDeliveryModeCode;
    const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
    const cartline = props.data.cartline;
    let hasShippingMethod = false;
    if (channelDeliveryOptionConfig !== undefined) {
      hasShippingMethod = !!(
        cartline?.DeliveryMode &&
        channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(
          (deliveryMode: any) => deliveryMode !== cartline?.DeliveryMode,
        )
      );
    } else {
      hasShippingMethod = !!(
        cartline?.DeliveryMode &&
        cartline?.DeliveryMode !== pickupDeliveryModeCode
      );
    }
    if (!hasShippingMethod) {
      return undefined;
    }

    const shippingChargeLines = (cartline?.ChargeLines || []).filter(
      (chargeLine: any) => chargeLine.IsShipping,
    );

    if (shippingChargeLines.length === 0) {
      return undefined;
    }

    const freightFee = shippingChargeLines.reduce(
      (chargeTotal: any, chargeLine: any) => {
        return chargeTotal + (chargeLine.CalculatedAmount || 0);
      },
      0,
    );

    const priceComponent = (
      <PriceComponent
        data={{
          price: {
            CustomerContextualPrice: freightFee,
          },
        }}
        freePriceText="Free"
        context={props.context}
        id={props.id}
        typeName={props.typeName}
        className="msc-cart-line__freight-actual"
        currencyCode={props.currencyCode}
      />
    );
    return (
      <>
        <label className="msc-cart-line__freight-label">
          {`${props.resources.shippingChargesText}:`}
        </label>
        <span className="shipping-value">{priceComponent}</span>
      </>
    );
  },
  renderOtherCharges: (props: any) => {
    const cartline = props.data.cartline;
    const otherCharges = cartline?.ChargeLines?.filter(
      (chargeline: any) => !chargeline?.IsShipping,
    );

    return (
      (otherCharges &&
        otherCharges.map((otherCharge: any, key: any) => {
          return otherCharge && otherCharge.CalculatedAmount ? (
            <div className="msc-cart-line__other-charges" key={key}>
              <label className="msc-cart-line__other-charges-label">
                {`${otherCharge?.Description}:`}
              </label>
              <span className="other-charge-value">
                <PriceComponent
                  data={{
                    price: {
                      CustomerContextualPrice: otherCharge?.CalculatedAmount,
                    },
                  }}
                  context={props.context}
                  id={props.id}
                  typeName={props.typeName}
                  className="msc-cart-line__other-charges-actual"
                  currencyCode={props.currencyCode}
                />
              </span>
            </div>
          ) : (
            ""
          );
        })) ||
      undefined
    );
  },
};

// tslint:disable-next-line:max-func-body-length
const CartLine: React.FC<ICartLineProps> = (props: any) => {
  let {
    productUrl,
    resources,
    allProps,
    atp,
    callFromCart,
    stopAtp,
    atpIndexDate,
  } = props;

  const {product, cartline} = props.data;
  const defaultImageSettings: IImageSettings = {
    viewports: {
      xs: {q: `w=300&h=300&m=6`, w: 0, h: 0},
      lg: {q: `w=300&h=300&m=6`, w: 0, h: 0},
      xl: {q: `w=300&h=300&m=6`, w: 0, h: 0},
    },
    lazyload: true,
    cropFocalRegion: true,
  };

  const destructDimensionStrings = {
    sizeString: resources?.sizeString,
    colorString: resources?.colorString,
    styleString: resources?.styleString,
    configString: resources?.configString,
    amountString: resources?.amountString,
  };
  const fallbackImageUrl =
    product &&
    getFallbackImageUrl(
      product.ItemId,
      props.context.actionContext.requestContext.apiSettings,
    );
  const imageSettings = defaultImageSettings || props.imageSettings;
  imageSettings.cropFocalRegion =
    !ObjectExtensions.isNullOrUndefined(imageSettings);
  // const { inputQuantityAriaLabel } = props.resources;
  const productDimensions =
    product &&
    CartLineItemFunctions.renderProductDimensions(
      product,
      destructDimensionStrings,
    );
  const renderDisountLines = CartLineItemFunctions?.renderDiscountLines(props);
  const renderShippingLabel = CartLineItemFunctions.renderShippingLabel(props);
  const renderOtherCharges = CartLineItemFunctions.renderOtherCharges(props);
  const payLoad = getPayloadObject(
    "click",
    props.telemetryContent!,
    "",
    product?.RecordId.toString(),
  );
  const prodAttribute = getTelemetryAttributes(
    props.telemetryContent!,
    payLoad,
  );
  const productName = product?.Name || cartline?.Description;
  /*Saqlain Customization Start - Added Product URL for each Product*/
  productUrl = getProductPageUrlSync(
    productName,
    product?.RecordId,
    props.context.actionContext,
  );
  /*Saqlain Customiation End*/
  const discountAmountCalculate = (discountLines: any): any => {
    var sum: Number = 0;
    discountLines.map((element: any) => {
      if (element.CustomerDiscountTypeValue !== 3) {
        sum += element?.EffectiveAmount;
      }
    });
    return sum;
  };
  if (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    typeof localStorage !== "undefined"
  ) {
    const [screenSize, setScreenSize] = React.useState(1);

    const [storeIndexes, setStoreIndexes] = React.useState<any[]>([]);
    React.useEffect(() => {
      setStoreIndexes([]);
    }, [callFromCart.toString()]);
    const _generateErrorMessage = (): JSX.Element | null => {
      /* 
              VSI CUSTOMIZATION 
              Maximum Quantity Limit has Precendence over other conditions/errors (minimum, multiple)
              Checks if maximum quanity of a product is reached, if yes returns error message. 
              Otherwise if there is any other error message, that is displayed
          */
      if (props.isCartIcon != true || props.isCartIcon == undefined) {
        if (props.maxQuantity) {
          if (props.currentQuantity >= props.maxQuantity) {
            return (
              <div className="msc-alert__header">
                <span className="msi-exclamation-triangle" />
                <span>{props.limitReached}</span>
              </div>
            );
          }
        }
      }

      if (props.errorMessage) {
        return (
          <div className="msc-alert__header">
            <span className="msi-exclamation-triangle" />
            <span>{props.errorMessage}</span>
          </div>
        );
      } else {
        return null;
      }
    };

    React.useEffect(() => {
      const _isMobile = _getScreenSize(props.context);
      setScreenSize(_isMobile);
    });

    const onChange = (newValue: number): boolean => {
      stopAtp();
      storeArray(props.data.product?.RecordId);

      if (props.quantityOnChange) {
        return props.quantityOnChange(props.data.cartLine, newValue);
      }
      return true;
    };
    const storeArray = (index: number) => {
      setStoreIndexes([...new Set([...storeIndexes, index])]);
    };
    const _getScreenSize = (context: ICoreContext): number => {
      // always render in mobile viewport on a mobile device
      if (
        context.request &&
        context.request.device &&
        context.request.device.Type === "Mobile"
      ) {
        return 1;
      }

      if (typeof window !== undefined && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
          if (gridSettings.xs && window.innerWidth < gridSettings.xs.w) {
            // mobile
            return 1;
          } else if (
            gridSettings.sm &&
            window.innerWidth <= gridSettings.sm.w
          ) {
            // iPad
            return 2;
          } else if (
            gridSettings.md &&
            window.innerWidth <= gridSettings.md.w
          ) {
            // Desktop
            return 3;
          } else if (
            gridSettings.lg &&
            window.innerWidth <= gridSettings.lg.w
          ) {
            // Desktop
            return 3;
          } else {
            return 3;
          }
        }
      }

      return 3; // desktop
    };

    const _generateSelectMenu = (
      quantity: number,
      currentQuantity: any | undefined,
    ): JSX.Element => {
      const errorMessage = _generateErrorMessage();
      // var status:boolean = false;

      return (
        <div className="product-quantity small-blks">
          {errorMessage ? [errorMessage] : null}
          <div className="mobile-quantity-and-atp-date-display">
            <div>
              <span className="mobile-label">Quantity: </span>
              <div>
                <IncrementalQuantity
                  id={`msc-cart-line__quantity_${props.data.product?.RecordId}-${props.data.cartLine?.DeliveryMode}-${props.data.cartLine?.LineId}`}
                  max={props.maxQuantity || 10}
                  currentCount={props.currentQuantity}
                  onChange={onChange}
                  inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                  decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                  incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                  key={props.data.cartLine.LineId}
                  disabled={!props.isCartStateReady}
                  isGiftCard={props.data.product.IsGiftCard}
                  telemetryContent={props.telemetryContent}
                />
              </div>
            </div>
            {/* {screenSize === 1 || screenSize === 2 ? (
                <div className="product-atp small-blks">
                  <label className="mobile-label">
                    {allProps === undefined
                      ? ""
                      : allProps.resources.atpCartTitle}
                  </label>
                  10-02-2023
                </div>
              ) : (
                <></>
              )} */}
          </div>
        </div>
      );
    };

    const _generateQuantityAndPrice = (): JSX.Element[] | null => {
      const nodes = [];

      if (!props.data.cartLine) {
        props.data.cartLine = props.data.cartline;
      } else {
        props.data.cartline = props.data.cartLine;
      }

      var discountArray = props.data.cartline?.Price;

      props.data.cartline?.DiscountLines.map((ele: any) => {
        discountArray = discountArray - (discountArray * ele.Percentage) / 100;
      });

      if (
        typeof window !== "undefined" &&
        typeof document !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        if (props.data?.cartline?.IsInvoiceLine == false) {
          if (props.isQuantityEditable && !props.data.product?.IsGiftCard) {
            nodes.push(
              <div className="product_price test small-blks">
                <span className="mobile-label">{props.priceTranslation}: </span>
                {props.data.cartline.LinePercentageDiscount > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data?.cartline.Price),
                    )}
                  </s>
                ) : props.data.cartline.DiscountLines?.length > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data?.cartline.Price),
                    )}
                  </s>
                ) : (
                  <React.Fragment>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data.cartline.Price),
                    )}
                  </React.Fragment>
                )}
                {/* this */}
                <h6>
                  {props.data.cartline.LinePercentageDiscount > 0 &&
                  props.data.cartline.DiscountLines.length == 0
                    ? props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) -
                          (Number(
                            Math.round(
                              (props.data?.cartline?.Price + Number.EPSILON) *
                                100,
                            ) / 100,
                          ) *
                            props.data.cartline?.LinePercentageDiscount) /
                            100,
                      )
                    : props.data.cartline.DiscountLines?.length > 0
                    ? props.context.cultureFormatter.formatCurrency(
                        (Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) *
                          Number(props.data.cartline.Quantity) -
                          discountAmountCalculate(
                            props.data.cartline.DiscountLines,
                          )) /
                          Number(props.data.cartline.Quantity),
                      )
                    : null}
                </h6>
              </div>,
            );
            nodes.push(
              _generateSelectMenu(
                props.maxQuantity || 10,
                props.currentQuantity,
              ),
            );
          } else {
            nodes.push(
              <div className="product_price small-blks">
                {" "}
                {
                  <span className="mobile-label">
                    {props?.priceTranslation}:{" "}
                  </span>
                }
                {props.data.cartline.LinePercentageDiscount > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data.cartline?.Price),
                    )}
                  </s>
                ) : props.data.cartline.DiscountLines?.length > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data.cartline?.Price),
                    )}
                  </s>
                ) : (
                  <React.Fragment>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data.cartline?.Price),
                    )}
                  </React.Fragment>
                )}
                <h6>
                  {props.data.cartline.LinePercentageDiscount > 0 &&
                  props.data.cartline.DiscountLines.length == 0
                    ? props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) -
                          (Number(
                            Math.round(
                              (props.data?.cartline?.Price + Number.EPSILON) *
                                100,
                            ) / 100,
                          ) *
                            props.data.cartline?.LinePercentageDiscount) /
                            100,
                      )
                    : props.data.cartline.DiscountLines?.length > 0
                    ? props.context.cultureFormatter.formatCurrency(
                        (Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) *
                          Number(props.data.cartline.Quantity) -
                          discountAmountCalculate(
                            props.data.cartline.DiscountLines,
                          )) /
                          Number(props.data.cartline.Quantity),
                      )
                    : null}
                </h6>
                {/* {props.data.cartline?  currencyFormatter.format(
                                              Number(Math.round((props.data?.cartline?.Price + Number.EPSILON) * 100) / 100),
                                              {
                                                code: props.currencyCode,
                                                decimal: ".",
                                                precision: 2,
                                                thousand:","
                                              }
                                            ): currencyFormatter.format(
                                              Number(Math.round((props.data?.cartline?.Price + Number.EPSILON) * 100) / 100),
                                              {
                                                code: props.currencyCode,
                                                decimal: ".",
                                                precision: 2,
                                                thousand:","
                                              }
                                            )} */}
              </div>,
            );

            nodes.push(
              <div className="msc-cart-line__quantity small-blks">
                <label className="mobile-label">
                  {resources.quantityDisplayString}:
                </label>

                <span className="quantity-value">
                  {props.data.cartline
                    ? props.data.cartline.Quantity
                    : props.data.cartLine.Quantity}
                </span>
              </div>,
            );
          }
        }
        nodes.push(
          <div className="product-subtotal small-blks">
            <span className="mobile-label">Sub Total: </span>
            {window.location.href.includes("orderdetails") ? (
              props.data.cartline ? (
                props.data.cartline.LinePercentageDiscount > 0 &&
                props.data.cartline.DiscountLines?.length == 0 ? (
                  <div>
                    <s>
                      {props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) * Number(props.data.cartline.Quantity),
                      )}
                    </s>
                    <br />

                    <span className="msc-price__actual">
                      {props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) *
                          Number(props.data.cartline.Quantity) -
                          discountAmountCalculate(
                            props.data.cartline.DiscountLines,
                          ),
                      )}
                    </span>
                  </div>
                ) : props.data.cartline.DiscountLines?.length > 0 ? (
                  <div>
                    <s>
                      {props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) * Number(props.data.cartline.Quantity),
                      )}
                    </s>
                    <br />
                    <span className="msc-price__actual">
                      {props.context.cultureFormatter.formatCurrency(
                        Number(
                          Math.round(
                            (props.data?.cartline?.Price + Number.EPSILON) *
                              100,
                          ) / 100,
                        ) *
                          Number(props.data.cartline.Quantity) -
                          discountAmountCalculate(
                            props.data.cartline.DiscountLines,
                          ),
                      )}
                    </span>
                  </div>
                ) : (
                  <span className="msc-price__actual">
                    {props.context.cultureFormatter.formatCurrency(
                      Number(props.data?.cartline?.Price) *
                        Number(props.data.cartline.Quantity),
                    )}
                  </span>
                )
              ) : props.data.cartLine.LinePercentageDiscount > 0 ? (
                <div>
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) * Number(props.data.cartLine.Quantity),
                    )}
                  </s>
                  <br />
                  <span className="msc-price__actual">
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) *
                        Number(props.data.cartLine.Quantity) -
                        discountAmountCalculate(
                          props.data.cartLine.DiscountLines,
                        ),
                    )}
                  </span>
                </div>
              ) : props.data.cartLine.DiscountLines?.length > 0 ? (
                <div>
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) * Number(props.data.cartline.Quantity),
                    )}
                  </s>
                  <br />
                  <span className="msc-price__actual">
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) *
                        Number(props.data.cartLine.Quantity) -
                        Number(
                          discountAmountCalculate(
                            props.data.cartLine.DiscountLines,
                          ),
                        ),
                    )}
                  </span>
                </div>
              ) : (
                <span className="msc-price__actual">
                  {props.context.cultureFormatter.formatCurrency(
                    Number(props.data?.cartLine.Price) *
                      Number(props.data.cartLine.Quantity),
                  )}
                </span>
              )
            ) : null}

            {window.location.href.includes("orderdetails") == false ? (
              <div>
                {props.data.cartline.LinePercentageDiscount > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) * Number(props.data?.cartline?.Quantity),
                    )}
                  </s>
                ) : props.data.cartline.DiscountLines?.length > 0 ? (
                  <s>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) * Number(props.data?.cartline?.Quantity),
                    )}
                  </s>
                ) : (
                  <React.Fragment>
                    {props.context.cultureFormatter.formatCurrency(
                      Number(
                        Math.round(
                          (props.data?.cartline?.Price + Number.EPSILON) * 100,
                        ) / 100,
                      ) * Number(props.data?.cartline?.Quantity),
                    )}
                  </React.Fragment>
                )}

                <h6>
                  {props.data.cartline.LinePercentageDiscount > 0 &&
                  props.data.cartline.DiscountLines?.length == 0
                    ? props.context.cultureFormatter.formatCurrency(
                        Number(
                          Number(
                            Math.round(
                              (props.data?.cartline?.Price + Number.EPSILON) *
                                100,
                            ) / 100,
                          ) *
                            props.data?.cartline?.Quantity -
                            discountAmountCalculate(
                              props.data.cartline.DiscountLines,
                            ),
                        ),
                      )
                    : props.data.cartline.DiscountLines?.length > 0
                    ? props.context.cultureFormatter.formatCurrency(
                        Number(
                          Number(
                            Math.round(
                              (props.data?.cartline?.Price + Number.EPSILON) *
                                100,
                            ) / 100,
                          ) *
                            Number(props.data?.cartline?.Quantity) -
                            discountAmountCalculate(
                              props.data.cartline.DiscountLines,
                            ),
                        ),
                      )
                    : null}
                </h6>
              </div>
            ) : null}
          </div>,
        );

        return nodes;
      } else {
        return [];
      }
      // No quantity selector for invoice line
    };

    if (props.displayMode === "COMPACT") {
      const reducedDimensions: string = product?.Dimensions
        ? product.Dimensions.reduce((str: any, productDimension: any) => {
            if (
              productDimension.DimensionValue &&
              productDimension.DimensionValue.Value
            ) {
              if (str) {
                return `${str}, ${productDimension.DimensionValue.Value}`;
              }

              return `${productDimension.DimensionValue.Value}`;
            }

            return str;
          }, "")
        : "";

      return (
        <div className="msc-cart-line msc-cart-line__compact">
          <div className="msc-cart-line__product-image">
            <Image
              src={props.primaryImageUrl || ""}
              fallBackSrc={fallbackImageUrl}
              altText={productName}
              gridSettings={props.gridSettings}
              imageSettings={imageSettings}
              loadFailureBehavior="empty"
            />
          </div>
          <div className="msc-cart-line__content">
            <a
              className="msc-cart-line__product-title"
              href={productUrl}
              {...prodAttribute}
            >
              {productName}
            </a>
            {reducedDimensions !== "" ? (
              <div className="msc-cart-line__product-variants">
                {destructDimensionStrings?.colorString}: {reducedDimensions}
              </div>
            ) : (
              ""
            )}
            {_generateQuantityAndPrice()}
          </div>
        </div>
      );
    }

    let filterAtpDate =
      atp?.length &&
      atp.filter(
        (obj: any, index: number, self: any) =>
          index ===
          self.findIndex(
            (item: any) => item.productRecordID === obj.productRecordID,
          ),
      );

    return (
      <div className="msc-cart-line">
        <div className="msc-cart-line__product-image">
          <Image
            src={props.primaryImageUrl || ""}
            fallBackSrc={fallbackImageUrl}
            altText={productName}
            gridSettings={props.gridSettings}
            imageSettings={imageSettings}
            loadFailureBehavior="empty"
          />
          <div className="product-info">
            <a
              className="msc-cart-line__product-title"
              href={productUrl}
              {...prodAttribute}
              onClick={() => {}}
            >
              {productName}
            </a>
            {renderDisountLines}
            {ArrayExtensions.hasElements(productDimensions) ? (
              <div className="msc-cart-line__product-variants">
                {productDimensions}
              </div>
            ) : (
              ""
            )}
          </div>
        </div>

        {props.showShippingChargesForLineItems && (
          <div className="msc-cart-line__freight">{renderShippingLabel}</div>
        )}
        {renderOtherCharges}
        {_generateQuantityAndPrice()}

        {(screenSize && screenSize === 1) || screenSize === 2 ? (
          <></>
        ) : (
          <div className="product-atp small-blks">
            <label className="mobile-label">
              {allProps === undefined ? "" : allProps.resources.atpCartTitle}
            </label>
            {filterAtpDate.map((item: any, index: any) => {
              let whatToShowInATPField: string = "";
              if (
                item &&
                item.atpDate !== "" &&
                storeIndexes.includes(props.data.product?.RecordId) &&
                item.currentQuantity
              ) {
                whatToShowInATPField = "NA";
              } else if (item) {
                if (item.atpDate === "" || item.currentQuantity === 0) {
                  whatToShowInATPField = "";
                } else {
                  // whatToShowInATPField = item.atpDate;
                  whatToShowInATPField = atpIndexDate;
                }
              }
              if (item.productRecordID === props.data.product?.RecordId) {
                return <span>{whatToShowInATPField}</span>;
              }
              return "";
            })}
          </div>
        )}
      </div>
    );
  } else {
    return <></>;
  }
};

export const CartLineItemAtpComponent: React.FunctionComponent<ICartLineProps> =
  msdyn365Commerce.createComponentOverride<any>("CartLineItem", {
    component: CartLine,
    ...CartLineItemFunctions,
  });

export default CartLineItemAtpComponent;
